import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
const categories = [{
  id: 'search',
  title: 'Local business enrichment',
  endpoints: [{
    name: 'Get local business details by Google Place ID',
    method: 'GET'
  }, {
    name: 'Get local business details by Openmart ID',
    method: 'GET'
  }, {
    name: 'Find local business from website',
    method: 'POST'
  }, {
    name: 'Search local businesses',
    method: 'POST'
  }, {
    name: 'Search local business IDs (fast)',
    method: 'POST'
  }],
  jsonSnippet: `// Request — array of Google Place IDs\nPOST /api/v1/search\n["ChIJd8BlQ2BZwokRAFUEcm_qrcA"]\n\n// Response (50+ fields per local business)\n{\n  "company_name": "Blue Bottle Coffee",\n  "company_type": "cafe",\n  "company_categories": ["Coffee Shop"],\n  "street_address": "123 Main St",\n  "city": "Los Angeles",\n  "state": "CA",\n  "zipcode": "90012",\n  "latitude": 34.0522,\n  "longitude": -118.2437,\n  "store_emails": ["hello@bluebottle.com"],\n  "company_phones": ["+1 (310) 555-0199"],\n  "website_url": "https://bluebottlecoffee.com",\n  "google_rating": 4.8,\n  "google_reviews_count": 1245,\n  "social_media_links": {\n    "INSTAGRAM": ["@bluebottle"],\n    "LINKEDIN": ["linkedin.com/company/..."]\n  },\n  "staffs": [{ "name": "J. Freeman", "role": "CEO" }],\n  "tags": ["cafe", "specialty coffee"]\n  // + 30 more fields\n}`
}, {
  id: 'enrichment',
  title: 'People enrichment',
  endpoints: [{
    name: 'Find decision makers',
    method: 'POST'
  }, {
    name: 'Detect tech stack',
    method: 'POST'
  }, {
    name: 'Find company emails',
    method: 'POST'
  }, {
    name: 'Enrich known people',
    method: 'POST'
  }],
  jsonSnippet: `// Request — find people (1-100 per batch)\nPOST /api/v1/task/batch/find_people\n[\n  {\n    "domain": "bluebottlecoffee.com",\n    "title": "CEO",\n    "max_k": 3,\n    "company_name": "Blue Bottle Coffee",\n    "city": "Los Angeles",\n    "state": "CA",\n    "country": "US",\n    "info_access": ["EMAIL", "PHONE"],\n    "notify_url": "https://your-webhook.com",\n    "tracking_id": "batch-001"\n  }\n]\n\n// Response\n{\n  "batch_id": "batch_x9y8z7",\n  "submit_for": "find_people",\n  "status": {\n    "total": 1,\n    "processing": 1,\n    "completed": 0,\n    "errored": 0,\n    "batch_ready": false\n  }\n}`
}, {
  id: 'batch',
  title: 'Batch & tasks',
  endpoints: [{
    name: 'Check batch status',
    method: 'GET'
  }, {
    name: 'Get task IDs in a batch',
    method: 'GET'
  }, {
    name: 'Get task result',
    method: 'GET'
  }],
  jsonSnippet: `// Request — check batch status\nGET /api/v1/batch/{batch_id}/status\n\n// Path parameter\nbatch_id: "a1b2c3d4-e5f6-7890-abcd-ef1234567890"\n\n// Response\n{\n  "total": 500,\n  "completed": 487,\n  "processing": 11,\n  "errored": 2,\n  "batch_ready": false\n}`
}];
export function FieldCoverage() {
  const [activeId, setActiveId] = useState(categories[0].id);
  const activeCategory = categories.find(c => c.id === activeId)!;
  const highlightJson = (str: string) => {
    return str.split('\n').map((line, i) => {
      if (line.match(/^(GET|POST|PUT|DELETE)\s/)) {
        const parts = line.match(/^(GET|POST|PUT|DELETE)\s(.+)/);
        if (parts) {
          return <div key={i} data-id="element-1757">
              <span className={parts[1] === 'GET' ? 'text-green-400 font-bold' : 'text-purple-400 font-bold'} data-id="element-1758">
                {parts[1]}
              </span>
              <span className="text-blue-300" data-id="element-1759"> {parts[2]}</span>
            </div>;
        }
      }
      if (line.trim().startsWith('//')) {
        return <div key={i} className="text-slate-500 mt-2" data-id="element-1760">
            {line}
          </div>;
      }
      const keyMatch = line.match(/"([^"]+)":/);
      const strMatch = line.match(/: "([^"]+)"/);
      const numMatch = line.match(/: ([0-9.]+)/);
      const boolMatch = line.match(/: (true|false)/);
      let formatted = line;
      if (keyMatch) formatted = formatted.replace(`"${keyMatch[1]}":`, `<span class="text-blue-400">"${keyMatch[1]}"</span>:`);
      if (strMatch) formatted = formatted.replace(`: "${strMatch[1]}"`, `: <span class="text-green-400">"${strMatch[1]}"</span>`);
      if (numMatch) formatted = formatted.replace(`: ${numMatch[1]}`, `: <span class="text-yellow-400">${numMatch[1]}</span>`);
      if (boolMatch) formatted = formatted.replace(`: ${boolMatch[1]}`, `: <span class="text-purple-400">${boolMatch[1]}</span>`);
      return <div key={i} dangerouslySetInnerHTML={{
        __html: formatted
      }} data-id="element-1761" />;
    });
  };
  const methodColor = (method: string) => {
    if (method === 'GET') return 'text-green-600 bg-green-50';
    if (method === 'DELETE') return 'text-red-600 bg-red-50';
    return 'text-purple-600 bg-purple-50';
  };
  return <section className="py-24 bg-white" data-id="element-1762">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1763">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1764">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight" data-id="element-1765">
            Powerful{' '}
            <span className="text-blue-600" data-id="element-1766">local business search API</span>
          </h2>
          <p className="text-xl text-slate-600 leading-relaxed" data-id="element-1767">
            Our local business search API lets you find, enrich, and manage
            local business data at scale. Every endpoint returns structured JSON
            you can store and use however you want.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-3 mb-14" data-id="element-1768">
          {categories.map(category => {
          const isActive = activeId === category.id;
          return <button key={category.id} onClick={() => setActiveId(category.id)} className={`px-6 py-3 rounded-full text-base font-semibold transition-all duration-200 ${isActive ? 'bg-slate-900 text-white' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`} data-id="element-1769">
                {category.title}
              </button>;
        })}
        </div>

        <div className="max-w-5xl mx-auto" data-id="element-1770">
          <AnimatePresence mode="wait" data-id="element-1771">
            <motion.div key={activeId} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.2
          }} className="grid grid-cols-1 md:grid-cols-[2fr_3fr] gap-14 items-start" data-id="element-1772">
              <div data-id="element-1773">
                <h3 className="text-2xl font-bold text-slate-900 mb-6" data-id="element-1774">
                  {activeCategory.title}
                </h3>
                <div className="space-y-3" data-id="element-1775">
                  {activeCategory.endpoints.map((endpoint, i) => <div key={i} className="flex items-center justify-between gap-4 py-3 px-4 bg-slate-50 rounded-xl" data-id="element-1776">
                      <span className="font-medium text-slate-700" data-id="element-1777">
                        {endpoint.name}
                      </span>
                      <span className={`text-xs font-bold px-2.5 py-1 rounded-full flex-shrink-0 ${methodColor(endpoint.method)}`} data-id="element-1778">
                        {endpoint.method}
                      </span>
                    </div>)}
                </div>
              </div>

              <div className="bg-slate-900 rounded-2xl p-6 font-mono text-sm leading-relaxed overflow-x-auto" data-id="element-1779">
                <div className="text-slate-500 mb-4 text-xs uppercase tracking-widest font-bold" data-id="element-1780">
                  Example request
                </div>
                <pre className="m-0 text-slate-300" data-id="element-1781">
                  {highlightJson(activeCategory.jsonSnippet)}
                </pre>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>;
}